import { useState, useEffect } from "react";
import { Form, Row, Col, Button, Container, ListGroup, Accordion, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FormContainer from '../components/FormContainer.jsx';
import { saveShippingAddress } from "../slices/cartSlice.js";
import { useGetUserAddressesQuery, useAddUserAddressMutation, useGetShippablePincodesQuery, useValidatePincodeMutation } from "../slices/addressApiSlice.js";
import CheckoutSteps from "../components/CheckoutSteps.jsx";
import Loader from "../components/Loader.jsx";
import Message from "../components/Message";
import { Typeahead } from "react-bootstrap-typeahead";
import { toast } from "react-toastify";

const ShippingScreen = () => {
  const {data: addresses, isLoading, error} = useGetUserAddressesQuery();
  const {data: postalCodes = [], isLoading: isLoadingPostalCodes} = useGetShippablePincodesQuery();
  const [ validatePincode ] = useValidatePincodeMutation();
  const cart = useSelector((state) => state.cart);
  const {shippingAddress} = cart;
  const [selectedAddressId, setSelectedAddressId] = useState(shippingAddress?._id || '');
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [addingUserAddress, setAddingUserAddress] = useState(false);
  const [addUserAddress, {isLoading: isLoadingAddUserAddress, error: isErrorAddUserAddress}] = useAddUserAddressMutation();

  const [name, setName] = useState(shippingAddress?.name || '');
  const [addressLine1, setAddressLine1] = useState(shippingAddress?.addressLine1 || '');
  const [addressLine2, setAddressLine2] = useState(shippingAddress?.addressLine2 || '');
  const [city, setCity] = useState(shippingAddress?.city || '');
  const [state, setState] = useState(shippingAddress?.state || '');
  const [postalCode, setPostalCode] = useState(shippingAddress?.postalCode || '');
  const [phone, setPhone] = useState(shippingAddress?.phone || '');

  const [selectedPin, setSelectedPin] = useState('');
  const [isPincodeConfirmed, setIsPincodeConfirmed] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Prevent browser autofill interference on inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      document.querySelectorAll('input').forEach((el) => {
        if (
          el.matches('[autocomplete="off"], [autocomplete="new-password"], [autocomplete^="new-"]')
        ) {
          el.value = ''; // Clear any autofilled text
        }
      });
    }, 500); // wait half a second for Chrome autofill to apply

    return () => clearTimeout(timer);
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();
    const newAddress = {
      _id: selectedAddressId,
      name,
      addressLine1,
      addressLine2,
      city,
      state,
      postalCode,
      phone
    };

    try {
      await validatePincode(postalCode).unwrap();
    } catch (err) {
      toast.error('Pincode not serviceable', {autoClose: 2000});
      return;
    }

    dispatch(saveShippingAddress(newAddress));
    if (!selectedAddressId) {
      try {
        setAddingUserAddress(true);
        const addAddress = await addUserAddress({ shippingAddress: newAddress }).unwrap();
        dispatch(saveShippingAddress(addAddress));
      } catch (err) {
        console.error('Failed to add address:', err);
        return;
      } finally {
        setAddingUserAddress(false);
      }
    }
    navigate('/placeorder');
  };

  const isFormValid = 
    name.trim() &&
    addressLine1.trim() &&
    city.trim() &&
    state.trim() &&
    postalCode &&
    phone;

  const addressStates = [
    {code:'', name:'Select State'},
    {code:'TG', name:'Telangana'},
    //{code:'AP', name:'Andhra Pradesh'},
    //{code:'TN', name:'Tamilnadu'},
    //{code:'KA', name:'Karnataka'},
    //{code:'MH', name:'Maharashtra'},
    //{code:'KL', name:'Kerala'}
  ];
  
  return (
    <FormContainer>
      <CheckoutSteps step1 step2/>
      <h2>Shipping</h2>
      {isLoading ? <Loader/> : error ? (
        <Message variant="danger">
          {error?.data?.message || error.error}
        </Message>
      ) : (
      <Container className="mb-4 p-3 border-0 ps-0">
        {addresses.length > 0 &&
        <>
        <h5 className="mb-2">Select a saved address</h5>
        <ListGroup variant="flush" className="mb-3">
          {selectedAddressId ? (
            <ListGroup.Item className="border-0 ps-0">
              <Form.Check
                type="radio"
                id={selectedAddressId}
                name="address"
                value={selectedAddressId}
                checked
                onChange={() => {}}
                label={
                  (() => {
                    const selected = addresses.find(a => a._id === selectedAddressId);
                    return selected ? (
                      <>
                        <strong>{selected.name}</strong><br />
                        {selected.addressLine1}{selected.addressLine2 && <br />}
                        {selected.addressLine2}<br />
                        {selected.city}, {selected.postalCode}<br />
                        {selected.state}<br />
                        {selected.phone}
                      </>
                    ) : 'Selected address not found';
                  })()
                }
              />
            </ListGroup.Item>
          ) : (
            <ListGroup.Item className="border-0">
              No address selected
            </ListGroup.Item>
          )}
          {addresses.length >= 1 &&
          <>
          <Accordion activeKey={activeKey} onSelect={(key) => setActiveKey(key)}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Show Addresses</Accordion.Header>
              <Accordion.Body>
                <ListGroup variant="flush">
                  {addresses
                  .filter((addr) => addr._id !== selectedAddressId)
                  .map((address) => (
                    <ListGroup.Item key={address._id} className="border-0 ps-0">
                      <Form.Check
                        type="radio"
                        id={address._id}
                        name="address"
                        value={address._id}
                        checked={selectedAddressId === address._id}
                        onChange={() => {
                          setSelectedAddressId(address._id);
                          setShowNewAddressForm(false);
                          setName(address.name);
                          setAddressLine1(address.addressLine1);
                          setAddressLine2(address.addressLine2);
                          setCity(address.city);
                          setState(address.state);
                          setPostalCode(address.postalCode);
                          setPhone(address.phone);
                          setActiveKey(null);
                        }}
                        label={
                          <>
                            <strong>{address.name}</strong><br />
                            {address.addressLine1}{address.addressLine2 && <br />}
                            {address.addressLine2}<br />
                            {address.city}, {address.postalCode}<br />
                            {address.state}<br />
                            {address.phone}
                          </>
                        }
                      />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          </>
          }
        </ListGroup>
        <div>
          {selectedAddressId &&
          <Button
            type="submit"
            variant="primary"
            className="py-2"
            onClick={submitHandler}
            disabled={isLoadingAddUserAddress}>
            Continue with selected address
          </Button>
          }
        </div>
        </>
        }
        <div className="mt-3">
          <Button
            className="mb-3 btn-light-primary"
            onClick={() => {
              setShowNewAddressForm(true);
              setSelectedAddressId('');
              setName('');
              setAddressLine1('');
              setAddressLine2('');
              setCity('');
              setState('');
              setPostalCode('');
              setPhone('');
            }}>
            Add New Address
          </Button>
          {isLoadingAddUserAddress && <Loader />}
          {isErrorAddUserAddress && <Message variant="danger">{isErrorAddUserAddress.message}</Message>}
        </div>
          {showNewAddressForm && (
            <>
              <Form
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                onSubmit={submitHandler}
                className="no-autofill"
              >
                <Form.Group controlId="name" className="my-2">
                    <Form.Label>Name <span className="text-danger"><strong>*</strong></span></Form.Label>
                    <Form.Control
                        type="text"
                        name="customName"
                        placeholder="Enter name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoComplete="new-name"
                        autoCorrect="off"
                        spellCheck={false}>
                    </Form.Control>
                </Form.Group>
                <Form.Group controlId="addressLine1" className="my-2">
                    <Form.Label>Address Line 1 <span className="text-danger"><strong>*</strong></span></Form.Label>
                    <Form.Control
                        type="text"
                        name="customAddressLine1"
                        placeholder="Enter address line 1"
                        value={addressLine1}
                        onChange={(e) => setAddressLine1(e.target.value)}
                        required
                        autoComplete="new-address-line-1"
                        autoCorrect="off"
                        spellCheck={false}>
                    </Form.Control>
                </Form.Group>
                <Form.Group controlId="addressLine2" className="my-2">
                    <Form.Label>Address Line 2</Form.Label>
                    <Form.Control
                        type="text"
                        name="customAddressLine2"
                        placeholder="Enter address line 2"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                        autoComplete="new-address-line-2"
                        autoCorrect="off"
                        spellCheck={false}>
                    </Form.Control>
                </Form.Group>
                <Form.Group controlId="city" className="my-2">
                    <Form.Label>City <span className="text-danger"><strong>*</strong></span></Form.Label>
                    <Form.Control
                        type="text"
                        name="customCity"
                        placeholder="Enter city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                        autoComplete="new-city"
                        autoCorrect="off"
                        spellCheck={false}>
                    </Form.Control>
                </Form.Group>
                <Form.Group controlId="state" className="my-2">
                    <Form.Label>State <span className="text-danger"><strong>*</strong></span></Form.Label>
                    <Form.Control
                        as="select"
                        name="customState"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        required
                        autoComplete="new-state"
                        autoCorrect="off"
                        spellCheck={false}>
                        {addressStates.map(({ code, name }) => (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        ))}
                    </Form.Control>
                </Form.Group>
                <Form.Group controlId="postalCode" className="my-2">
                    <Form.Label>
                      Postal Code <span className="text-danger"><strong>*</strong></span> <span className="text-info">Deliverability is based on pincode. So, please confirm pincode</span>
                    </Form.Label>
                    <Row>
                      <Col>
                        <Typeahead
                          id="postalCode"
                          labelKey="pinCode"
                          options={postalCodes}
                          placeholder="Select or type postal code"
                          selected={selectedPin ? [{ pinCode: selectedPin }] : []}
                          onInputChange={(text) => {
                            setPostalCode(text);
                            setIsPincodeConfirmed(false);
                          }}
                          onChange={(selected) => {
                            setIsPincodeConfirmed(false);
                            const newPin = selected[0]?.pinCode || '';
                            setSelectedPin(newPin);
                            if (newPin) {
                              setPostalCode(newPin);
                              setIsPincodeConfirmed(true); // auto-confirm if user picks manually
                            }
                          }}
                          isLoading={isLoadingPostalCodes}
                          allowNew={false}
                          required
                          inputProps={{
                            name: `customPostalCode_${Math.random().toString(36).slice(2)}`,
                            autoComplete: 'new-postal-code',
                            autoCorrect: 'off',
                            spellCheck: 'false',
                            inputMode: 'numeric'
                          }}
                        />
                      </Col>
                      <Col>
                        <Button
                          variant={isPincodeConfirmed ? "success" : "primary"}
                          className="w-100"
                          disabled={!postalCode || isPincodeConfirmed}
                          onClick={async () => {
                            const match = postalCodes.find((p) => p.pinCode === postalCode);
                            if (!match) {
                              toast.error("Pincode not serviceable", {autoClose: 2000});
                              setIsPincodeConfirmed(false);
                              return;
                            }
                            // if (!selectedPin) {
                            //   toast.warn("Please select a postal code first.");
                            //   return;
                            // }
                            // setPostalCode(selectedPin);
                            setIsPincodeConfirmed(true);
                          }}
                        >
                          {isPincodeConfirmed ? "Pincode Confirmed" : "Confirm Pincode"}
                        </Button>
                      </Col>
                    </Row>
                </Form.Group>
                <Form.Group controlId="phone" className="my-2">
                    <Form.Label>Phone Number <span className="text-danger"><strong>*</strong></span></Form.Label>
                    <Form.Control
                        type="tel"
                        name="customPhone"
                        inputMode="numeric"
                        pattern="[1-9][0-9]{9}"
                        placeholder="Enter valid 10 digit phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        autoComplete="new-phone"
                        autoCorrect="off"
                        spellCheck={false}>
                    </Form.Control>
                </Form.Group>
                <Button
                  type="submit"
                  variant="primary"
                  className="py-2 mt-2"
                  disabled={!isFormValid || isLoadingAddUserAddress || addingUserAddress}>
                  {addingUserAddress ? (
                    <>
                      <Spinner animation="border" size="sm" /> Adding new address...
                    </>
                  ) : (
                    'Add new address and continue'
                  )}
                </Button>
              </Form>
            </>
          )}
          {/* <Form.Group controlId="sameAsBilling" className="mt-2">
            <Form.Check 
              type="radio"
              label="Billing address is same as shipping address"
              defaultChecked
            />
          </Form.Group> */}
        </Container>
      )}
    </FormContainer>
  );
}

export default ShippingScreen